import React, { useState } from "react";
import Dialog from "../../../../../common/element/Dialog/Dialog";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { useDispatch } from "react-redux";
import { PayClientInvoice } from "../../../../../actions/client-portal/invoices/PayClientInvoice";
import ThreeDotLoader from "../../../../../common/element/ButtonLoader/ThreeDotLoader";
import moment from "moment";

const ClientInvoicePaymentModal = ({ isOpen, onClose, invoice, onSuccess }) => {
  const dispatch = useDispatch();
  const stripe = useStripe();
  const elements = useElements();

  const [paymentMethodChoice, setPaymentMethodChoice] = useState("card"); // "card" | "payto"
  const [paytoIdType, setPaytoIdType] = useState("payid"); // "payid" | "bsb"
  const [paytoPayId, setPaytoPayId] = useState("");
  const [paytoBsb, setPaytoBsb] = useState("");
  const [paytoAccountNumber, setPaytoAccountNumber] = useState("");
  const [paytoAccountName, setPaytoAccountName] = useState("");
  const [paytoAgree, setPaytoAgree] = useState(false);

  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  if (!isOpen || !invoice) return null;

  const invoiceAmount = invoice?.amount ? parseFloat(invoice.amount).toFixed(2) : "0.00";
  const invoiceNumber = invoice?.xero_invoice_number || `INV-${invoice?.client_xero_invoice_id}`;

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setError(null);
    setIsProcessing(true);

    try {
      if (paymentMethodChoice === "payto") {
        if (paytoIdType === "payid" && !paytoPayId.trim()) {
          setIsProcessing(false);
          setError("Please enter a valid PayID (Email, Mobile, or ABN).");
          return;
        }

        if (paytoIdType === "bsb") {
          const cleanBsb = paytoBsb.replace(/[^0-9]/g, "");
          if (!cleanBsb || cleanBsb.length !== 6) {
            setIsProcessing(false);
            setError("Please enter a valid 6-digit BSB (e.g. 123-456 or 123456).");
            return;
          }
          const cleanAccount = paytoAccountNumber.trim();
          if (!cleanAccount || cleanAccount.length < 5 || cleanAccount.length > 9) {
            setIsProcessing(false);
            setError("Please enter a valid Account Number (5 to 9 digits).");
            return;
          }
        }

        if (!paytoAgree) {
          setIsProcessing(false);
          setError("Please accept the PayTo agreement terms to proceed.");
          return;
        }

        const payload = {
          client_xero_invoice_id: invoice.client_xero_invoice_id,
          payment_method_type: "payto",
          payto_type: paytoIdType,
          payto_payid: paytoPayId.trim(),
          payto_bsb: paytoBsb.trim(),
          payto_account_number: paytoAccountNumber.trim(),
          payto_account_name: paytoAccountName.trim(),
        };

        const response = await dispatch(PayClientInvoice(payload));
        if (response?.success) {
          setPaymentSuccess(true);
          setTimeout(() => {
            if (onSuccess) onSuccess();
            onClose();
          }, 1500);
        } else {
          setError(response?.message || "PayTo payment failed. Please try again.");
        }
      } else {
        // Card Payment Flow
        if (!stripe || !elements) {
          setIsProcessing(false);
          setError("Payment service is initializing. Please try again.");
          return;
        }

        const cardElement = elements.getElement(CardElement);
        if (!cardElement) {
          setIsProcessing(false);
          setError("Card details element not ready.");
          return;
        }

        const { token, error: stripeError } = await stripe.createToken(cardElement);
        if (stripeError) {
          setIsProcessing(false);
          setError(stripeError.message);
          return;
        }

        const payload = {
          client_xero_invoice_id: invoice.client_xero_invoice_id,
          payment_method_type: "card",
          cardToken: token.id,
        };

        const response = await dispatch(PayClientInvoice(payload));
        if (response?.success) {
          setPaymentSuccess(true);
          setTimeout(() => {
            if (onSuccess) onSuccess();
            onClose();
          }, 1500);
        } else {
          setError(response?.message || "Card payment failed. Please verify your card details.");
        }
      }
    } catch (err) {
      setError(err?.message || "An unexpected error occurred during payment processing.");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      onRequestClose={onClose}
      width="640px"
      style={{ content: { maxWidth: "95vw" } }}
    >
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between pb-4 border-b border-gray-200">
          <div>
            <h3 className="text-xl font-bold text-gray-900">Pay Invoice</h3>
            <p className="text-sm text-gray-500 mt-0.5">
              Invoice <span className="font-semibold text-gray-800">{invoiceNumber}</span>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 focus:outline-none cursor-pointer"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {paymentSuccess ? (
          <div className="py-8 text-center space-y-3">
            <div className="w-12 h-12 rounded-full bg-green-100 mx-auto flex items-center justify-center text-green-600">
              <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h4 className="text-lg font-bold text-gray-900">Payment Successful!</h4>
            <p className="text-sm text-gray-600">
              Invoice {invoiceNumber} has been successfully paid and receipt generated.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="mt-4 space-y-5">
            {/* Amount Summary Box */}
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex items-center justify-between">
              <div>
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider block">
                  Amount Due
                </span>
                <div className="text-2xl font-bold text-slate-900 mt-0.5">
                  ${invoiceAmount} <span className="text-sm font-normal text-slate-500">AUD</span>
                </div>
              </div>
              <div className="text-right text-xs text-slate-500 space-y-0.5">
                <div>
                  Due Date:{" "}
                  <span className="font-medium text-slate-700">
                    {invoice?.due_date ? moment(invoice.due_date).format("DD/MM/YYYY") : "Immediate"}
                  </span>
                </div>
                <div>
                  Status: <span className="font-semibold text-amber-600">Unpaid</span>
                </div>
              </div>
            </div>

            {/* Payment Method Selector */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Select Payment Method
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {/* Card Option */}
                <div
                  onClick={() => {
                    setPaymentMethodChoice("card");
                    setError(null);
                  }}
                  className={`cursor-pointer p-4 border rounded-xl flex items-center gap-3 transition ${
                    paymentMethodChoice === "card"
                      ? "border-blue-600 bg-blue-50/50 shadow-sm ring-1 ring-blue-600"
                      : "border-gray-200 hover:border-gray-300 bg-white"
                  }`}
                >
                  <input
                    type="radio"
                    id="modal_method_card"
                    name="modal_payment_choice"
                    checked={paymentMethodChoice === "card"}
                    onChange={() => {
                      setPaymentMethodChoice("card");
                      setError(null);
                    }}
                    className="text-blue-600 focus:ring-blue-500 cursor-pointer"
                  />
                  <label htmlFor="modal_method_card" className="cursor-pointer">
                    <div className="flex items-center gap-2">
                      <svg className="w-5 h-5 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                      </svg>
                      <span className="font-semibold text-gray-900 text-sm">Credit / Debit Card</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">Instant Visa, Mastercard, AMEX</p>
                  </label>
                </div>

                {/* PayTo Option */}
                <div
                  onClick={() => {
                    setPaymentMethodChoice("payto");
                    setError(null);
                  }}
                  className={`cursor-pointer p-4 border rounded-xl flex items-center gap-3 transition ${
                    paymentMethodChoice === "payto"
                      ? "border-blue-600 bg-blue-50/50 shadow-sm ring-1 ring-blue-600"
                      : "border-gray-200 hover:border-gray-300 bg-white"
                  }`}
                >
                  <input
                    type="radio"
                    id="modal_method_payto"
                    name="modal_payment_choice"
                    checked={paymentMethodChoice === "payto"}
                    onChange={() => {
                      setPaymentMethodChoice("payto");
                      setError(null);
                    }}
                    className="text-blue-600 focus:ring-blue-500 cursor-pointer"
                  />
                  <label htmlFor="modal_method_payto" className="cursor-pointer">
                    <div className="flex items-center gap-2">
                      <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
                      </svg>
                      <span className="font-semibold text-gray-900 text-sm">PayTo Bank Payment</span>
                      <span className="bg-blue-100 text-blue-800 text-[10px] font-bold px-1.5 py-0.5 rounded">AU NPP</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">Real-time debit via PayID or BSB</p>
                  </label>
                </div>
              </div>
            </div>

            {/* Card Form */}
            {paymentMethodChoice === "card" ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-gray-800">Card Information</h4>
                  <span className="text-xs text-gray-500">Secured & encrypted via Stripe</span>
                </div>
                <div className="border border-gray-300 rounded-lg p-3 bg-white focus-within:border-blue-500 transition">
                  <CardElement
                    options={{
                      hidePostalCode: true,
                      disableAutofill: true,
                      style: {
                        base: {
                          fontSize: "14px",
                          color: "#333",
                          "::placeholder": {
                            color: "#a0aec0",
                          },
                        },
                        invalid: { color: "#e53e3e" },
                      },
                    }}
                  />
                </div>
              </div>
            ) : (
              /* PayTo Form matching YourContractor/payment/index.js */
              <div className="space-y-4">
                {/* PayTo Header */}
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-gray-800">Australian Bank Account (PayTo)</h4>
                  <span className="text-xs text-gray-500 font-normal">Real-Time NPP Payment</span>
                </div>

                {/* PayID vs BSB Selector Pills */}
                <div className="flex gap-2 p-1 bg-gray-100 rounded-lg w-fit">
                  <button
                    type="button"
                    onClick={() => setPaytoIdType("payid")}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-md transition cursor-pointer ${
                      paytoIdType === "payid"
                        ? "bg-white text-blue-700 shadow-sm"
                        : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    Pay with PayID
                  </button>
                  <button
                    type="button"
                    onClick={() => setPaytoIdType("bsb")}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-md transition cursor-pointer ${
                      paytoIdType === "bsb"
                        ? "bg-white text-blue-700 shadow-sm"
                        : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    Pay with BSB & Account
                  </button>
                </div>

                {paytoIdType === "payid" ? (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        PayID (Email, Mobile, or ABN) *
                      </label>
                      <input
                        type="text"
                        value={paytoPayId}
                        onChange={(e) => setPaytoPayId(e.target.value)}
                        placeholder="e.g. billing@company.com.au or 12 345 678 901"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500 bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Account Holder Name (Optional)
                      </label>
                      <input
                        type="text"
                        value={paytoAccountName}
                        onChange={(e) => setPaytoAccountName(e.target.value)}
                        placeholder="e.g. Your Company Pty Ltd"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500 bg-white"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          BSB Number *
                        </label>
                        <input
                          type="text"
                          value={paytoBsb}
                          onChange={(e) => setPaytoBsb(e.target.value)}
                          placeholder="e.g. 000-000"
                          maxLength={7}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500 bg-white"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Account Number *
                        </label>
                        <input
                          type="text"
                          value={paytoAccountNumber}
                          onChange={(e) => setPaytoAccountNumber(e.target.value)}
                          placeholder="e.g. 12345678"
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500 bg-white"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Account Name *
                      </label>
                      <input
                        type="text"
                        value={paytoAccountName}
                        onChange={(e) => setPaytoAccountName(e.target.value)}
                        placeholder="e.g. Your Company Pty Ltd"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500 bg-white"
                      />
                    </div>
                  </div>
                )}

                {/* PayTo Mandate Notice Box */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900 leading-relaxed">
                  <div className="font-semibold flex items-center gap-1.5 mb-1 text-blue-950">
                    <svg className="w-4 h-4 text-blue-700 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    PayTo Agreement (Mandate) Notice
                  </div>
                  <p className="mb-1 text-xs">
                    By proceeding, you agree to create a new PayTo payment mandate authorizing Conserve to debit <strong>AUD ${invoiceAmount}</strong> from your nominated Australian bank account for this invoice.
                  </p>
                  <p className="text-[11px] text-blue-800">
                    • <strong>Authorization:</strong> Please approve the mandate request in your mobile banking app or online banking when prompted.
                    <br />
                    • <strong>Management:</strong> You can view, pause, or cancel this PayTo agreement at any time in your banking app.
                    <br />
                    • <strong>Support:</strong> Questions? Contact Conserve support at <span className="font-medium">support@conserve.com.au</span> or call <span className="font-medium">1300 859 018</span>.
                  </p>
                </div>

                {/* PayTo Terms Agreement Checkbox */}
                <div className="flex items-start gap-2 pt-1">
                  <input
                    type="checkbox"
                    id="paytoTerms"
                    checked={paytoAgree}
                    onChange={(e) => setPaytoAgree(e.target.checked)}
                    className="mt-1 cursor-pointer text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor="paytoTerms" className="text-xs text-gray-700 cursor-pointer">
                    I accept the PayTo terms and authorize payment directly from my nominated bank account.
                  </label>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start justify-between">
                <p className="text-red-700 text-xs">{error}</p>
                {paymentMethodChoice === "payto" && (
                  <button
                    type="button"
                    onClick={() => {
                      setPaymentMethodChoice("card");
                      setError(null);
                    }}
                    className="text-xs font-semibold text-blue-700 hover:underline ml-2 flex-shrink-0 cursor-pointer"
                  >
                    Switch to Card
                  </button>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                disabled={isProcessing}
                className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none transition cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isProcessing || (paymentMethodChoice === "payto" && !paytoAgree)}
                className={`inline-flex items-center justify-center px-6 py-2 text-sm font-medium text-white rounded-lg shadow-sm focus:outline-none transition-all cursor-pointer ${
                  isProcessing || (paymentMethodChoice === "payto" && !paytoAgree)
                    ? "opacity-60 cursor-not-allowed"
                    : "hover:opacity-90"
                }`}
                style={{ backgroundColor: "#105296" }}
              >
                {isProcessing ? (
                  <span className="flex items-center gap-2">
                    <ThreeDotLoader />
                    <span>Processing...</span>
                  </span>
                ) : (
                  "Pay"
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </Dialog>
  );
};

export default ClientInvoicePaymentModal;
